import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose, withApollo } from 'react-apollo';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import {
  withRedux,
  withConnectLean,
  withRelatedVideos,
  withVideosStatus,
} from './wrappers';
import VideoResourcePreview from 'legacy/src/components/VideoResourcePreview';
import RelatedVideoItem from './RelatedVideoItem';
import ResourceTypes from 'legacy-packages/unit-outline/src/constants';
import { getResourceAssignedClasses } from 'legacy-packages/unit-outline/src/queries';
import { withCourseOutline } from 'chameleon-packages/units';
import getCourseOutlineResourcesByType from 'legacy-src/utility/helpers/getCourseOutlineResourcesByType';
import log from 'chameleon/ui-stack/utilities/log';

import './styles.scss';

const { EMBEDDED_VIDEO } = ResourceTypes;

const RelatedVideos = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSharedClasses, setIsLoadingSharedClasses] = useState(true);
  const {
    isLoadingRelatedVideos,
    isLoadingWatchedVideos,
    videos,
    watchedVideoIds,
    teacherClasses,
    router,
    masterSubjectId,
    userData,
    setSharedResourceWatchStatus,
    setVideoResourcesAssignedClasses,
    userData: { isStudent, isTeacher, isAdmin, isCoordinator },
    assignmentSectionId,
    isAssignedAtLeastOnce,
    itemId,
    client,
    isLoadingSubjectCourseOutline,
    showRelatedVideosCarousel,
    apdCarouselData,
    setAPDCarouselData: setAPDCarouselDataProp,
    updateSingleVideoAPDCarouselData,
    watchedVideosInSession,
    setWatchedVideosInSession,
    showZeroWatchedFlag,
    isImpersonating,
    courseOutline,
  } = props;

  const skipAssignedClassesQuery =
    isAdmin || isCoordinator || (videos || []).length === 0;
  const subjectCourseOutlineUnits = courseOutline.units;
  const allAvailableVideoResources = getCourseOutlineResourcesByType(
    subjectCourseOutlineUnits,
    ResourceTypes.EMBEDDED_VIDEO,
  );

  useEffect(() => {
    const loading =
      isLoadingRelatedVideos ||
      isLoadingSharedClasses ||
      isLoadingWatchedVideos ||
      isLoadingSubjectCourseOutline;

    setIsLoading(loading);
  }, [
    isLoadingRelatedVideos,
    isLoadingSharedClasses,
    isLoadingWatchedVideos,
    isLoadingSubjectCourseOutline,
  ]);

  useEffect(() => {
    if (!isLoadingSharedClasses) {
      setIsLoadingSharedClasses(true);
    }

    if (
      !isLoadingRelatedVideos &&
      !isLoadingWatchedVideos &&
      !skipAssignedClassesQuery
    ) {
      const classIds = isStudent
        ? [assignmentSectionId]
        : teacherClasses.map((section) => Number.parseInt(section.id, 10));

      client
        .query({
          query: getResourceAssignedClasses,
          variables: {
            subjectId: Number.parseInt(masterSubjectId, 10),
            classIds,
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          const { resources } = (data || {}).sharedCourseOutline || {};
          const resourcesWithAssignedClasses = resources || [];
          const allWatchedVideos = Array.from(
            new Set([
              ...(watchedVideosInSession || []),
              ...(watchedVideoIds || []),
            ]),
          );
          setVideoResourcesAssignedClasses(
            masterSubjectId,
            videos,
            resourcesWithAssignedClasses,
            allWatchedVideos,
            () => {
              setIsLoadingSharedClasses(false);
            },
          );
        })
        .catch((error) => log('error', error));
    }
  }, [itemId, isLoadingRelatedVideos, isLoadingWatchedVideos]);

  /*
   * This use effect is for avoid a race condition for admin/coords.
   * When dealing with admin/coords or we don't have related videos we might end in setting state too quickly.
   * */
  useEffect(() => {
    if (
      isLoadingSharedClasses &&
      !isLoadingRelatedVideos &&
      skipAssignedClassesQuery
    ) {
      setIsLoadingSharedClasses(false);
    }
  }, [isLoadingSharedClasses, isLoadingRelatedVideos]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!videos.length) {
    return null;
  }

  const assignmentSectionIds = [assignmentSectionId];
  const assignmentSections = isTeacher
    ? teacherClasses.filter(
        (section) => Number.parseInt(section.id, 10) === assignmentSectionId,
      )
    : [{ id: assignmentSectionId }];
  const filteredResources = videos.map((video) => ({
    ...video,
    __typename: EMBEDDED_VIDEO,
  }));
  const selectedVideo = allAvailableVideoResources.find(
    (video) => video.url === router.location.query.apd,
  );
  return (
    <Fragment>
      {selectedVideo && (
        <VideoResourcePreview
          withCarousel={showRelatedVideosCarousel}
          useFilterableResources
          loadingData={false}
          videoId={selectedVideo.url}
          videoResource={{
            resource: {
              ...selectedVideo,
              __typename: EMBEDDED_VIDEO,
            },
            unit: {
              unitId: selectedVideo.unitId,
            },
          }}
          userData={userData}
          subject={{ id: masterSubjectId }}
          userClasses={teacherClasses}
          filteredClasses={assignmentSections}
          router={router}
          updateSharedResourceWatchStatus={setSharedResourceWatchStatus}
          studentClasses={assignmentSections}
          allAvailableVideoResources={allAvailableVideoResources}
          filteredResources={filteredResources}
          loadingFilteredResources={false}
          filteredResourcesLabel="Videos Related to This Question"
          setAPDCarouselData={setAPDCarouselDataProp}
          apdCarouselData={apdCarouselData}
          updateSingleVideoAPDCarouselData={updateSingleVideoAPDCarouselData}
          setWatchedVideosInSession={setWatchedVideosInSession}
          watchedVideosInSession={watchedVideosInSession}
          showZeroWatchedFlag={showZeroWatchedFlag}
          isImpersonating={isImpersonating}
        />
      )}
      <div className="ft-related-videos-container">
        <h3 className="ft-rv-header" data-test-related-videos-header>
          Related Videos
        </h3>
        {videos.map(({ id, unitId, displayName, url }) => (
          <RelatedVideoItem
            id={id}
            key={id}
            scope={[
              'units',
              `unit-${unitId}`,
              'resources',
              `resource-${id}-subject-${masterSubjectId}`,
            ]}
            displayName={displayName}
            displayAssignedIcon={isAssignedAtLeastOnce}
            userClassIds={assignmentSectionIds}
            previewPath={`${router.location.pathname}?apd=${url}`}
          />
        ))}
      </div>
    </Fragment>
  );
};

RelatedVideos.displayName = 'RelatedVideos';

RelatedVideos.propTypes = {
  isLoadingRelatedVideos: PropTypes.bool,
  isLoadingWatchedVideos: PropTypes.bool,
  itemId: PropTypes.string.isRequired,
  isAssigned: PropTypes.bool,
  videos: PropTypes.array,
  educationPeriod: PropTypes.string,
  masterSubjectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  teacherClasses: PropTypes.array,
  userData: PropTypes.object.isRequired,
  assignmentSectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  watchedVideoIds: PropTypes.array,
  setSharedResourceWatchStatus: PropTypes.func.isRequired,
  showRelatedVideosCarousel: PropTypes.bool,
  watchedVideosInSession: PropTypes.array,
  setWatchedVideosInSession: PropTypes.func,
};

RelatedVideos.defaultProps = {
  isLoadingRelatedVideos: false,
  isLoadingWatchedVideos: false,
  isAssigned: false,
  videos: [],
  educationPeriod: null,
  teacherClasses: [],
  watchedVideoIds: [],
  showRelatedVideosCarousel: false,
  watchedVideosInSession: [],
  setWatchedVideosInSession: () => {},
};

export default compose(
  withApollo,
  withRedux,
  /**
   * withCourseOutline - needs userData - chosenEducationPeriodCode
   * @todo - check userData is present
   * */
  withCourseOutline(),
  withRelatedVideos,
  withVideosStatus,
  withConnectLean,
)(RelatedVideos);
